
import type React from "react"
import { useEffect, useRef } from "react"
import { Sidebar } from "@/components/sidebar"
import { Header } from "@/components/header"
import { AuthProvider, useAuth } from "@/contexts/auth-context"
import { SidebarProvider, SidebarInset, useSidebar } from "@/components/ui/sidebar"
import { useLocation } from "react-router-dom"
import { PUBLIC_PATHS } from "@/lib/routes"
import { Button } from "@/components/ui/button"
import { LogOut, Shield, ChevronRight } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import { Toaster } from "@/components/ui/sonner"
import { ConnectionStatus } from "@/components/connection-status"
import { ChunkErrorRecovery } from "@/components/chunk-error-recovery"
import { LocaleProvider, useTranslations } from "@/i18n"
import { PageHeaderSlotProvider } from "@/contexts/page-header-slot-context"

export function AppShell({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(r => r.unregister())
      })
    }
  }, [])

  return (
    <LocaleProvider>
      <AuthProvider>
        <LayoutContent>{children}</LayoutContent>
      </AuthProvider>
      <Toaster />
      <ConnectionStatus />
      <ChunkErrorRecovery />
    </LocaleProvider>
  )
}

function LayoutContent({ children }: { children: React.ReactNode }) {
  const pathname = useLocation().pathname
  const { user, isLoading, isAuthenticated } = useAuth()
  const t = useTranslations('common')
  const isPublicRoute = PUBLIC_PATHS.some(route => pathname.startsWith(route))
  const adminRoutes = ["/admin"]
  const isAdminRoute = adminRoutes.some(route => pathname.startsWith(route))

  // Always allow public routes
  if (isPublicRoute) {
    return <>{children}</>
  }

  // Don't render protected content until auth state is confirmed
  // This prevents any flash of protected content
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="relative">
            <div className="h-10 w-10 rounded-full border-2 border-muted mx-auto" />
            <div className="absolute inset-0 h-10 w-10 rounded-full border-2 border-transparent border-t-primary animate-spin mx-auto" />
          </div>
          <p className="text-sm text-muted-foreground mt-4">{t('loading')}</p>
        </div>
      </div>
    )
  }

  // If not authenticated, redirect (handled by auth context, but show nothing while redirecting)
  if (!isAuthenticated) {
    return null
  }

  // System admins still get the minimal admin layout when they explicitly
  // navigate under /admin, but they're free to use the regular WMS UI on
  // every other route — no forced redirect.
  if (user?.isSystemAdmin && isAdminRoute) {
    return (
      <div className="min-h-screen bg-background">
        <AdminHeader />
        <main className="p-4 md:p-6">{children}</main>
      </div>
    )
  }

  // Regular WMS interface (for non-system-admins, and for system admins on
  // any non-/admin path).
  return <MainLayout>{children}</MainLayout>
}

function MainLayout({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider
      style={
        {
          // Narrower than shadcn's stock 16rem / 3rem
          "--sidebar-width": "13rem",
          "--sidebar-width-icon": "3rem",
        } as React.CSSProperties
      }
    >
      <SidebarAutoCollapse threshold={1280} />
      <Sidebar />
      {/* `min-w-0` on SidebarInset (a flex item) overrides the default
          `min-width: auto`, so a wide descendant — e.g. the many-column
          item-list table in the stock-take details page — can't expand
          the inset past the viewport and trigger horizontal page scroll. */}
      <SidebarInset className="min-w-0">
        <PageHeaderSlotProvider>
          <Header />
          <div className="flex-1 min-w-0 p-4 md:p-6">{children}</div>
        </PageHeaderSlotProvider>
      </SidebarInset>
    </SidebarProvider>
  )
}

/**
 * Auto-collapse the sidebar when the viewport gets narrower than `threshold`.
 * Only fires on the wide→narrow transition (and once on mount if already
 * narrow), so manually opening the sidebar at narrow widths isn't overridden
 * by every subsequent resize tick.
 */
function SidebarAutoCollapse({ threshold }: { threshold: number }) {
  const { setOpen } = useSidebar()
  const lastWideRef = useRef(
    typeof window !== "undefined" ? window.innerWidth >= threshold : true,
  )

  useEffect(() => {
    // On mount: if we're already below the threshold, collapse.
    if (window.innerWidth < threshold) {
      setOpen(false)
      lastWideRef.current = false
    }

    const handleResize = () => {
      const isWide = window.innerWidth >= threshold
      // Only react when crossing the threshold from wide → narrow.
      if (lastWideRef.current && !isWide) {
        setOpen(false)
      }
      lastWideRef.current = isWide
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [setOpen, threshold])

  return null
}

function getInitials(name?: string | null, email?: string | null): string {
  if (name) {
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.slice(0, 2).toUpperCase()
  }
  if (email) {
    return email.slice(0, 2).toUpperCase()
  }
  return 'A'
}

function AdminHeader() {
  const { user, logout } = useAuth()
  const t = useTranslations('pages')
  const tAuth = useTranslations('auth')

  return (
    <header className="sticky top-0 z-30 border-b border-border/60 bg-background/80 backdrop-blur-md">
      <div className="flex items-center justify-between px-6 py-4">
        {/* Brand + Title */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground tracking-tight">{t('admin.title')}</h1>
              <p className="text-sm text-muted-foreground">{t('admin.description')}</p>
            </div>
          </div>
        </div>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-9 gap-2 px-2 hover:bg-muted/50 rounded-full"
            >
              <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-xs font-medium text-primary">
                  {getInitials(user?.fullName, user?.email)}
                </span>
              </div>
              <div className="hidden sm:flex flex-col items-start">
                <span className="text-sm font-medium text-foreground max-w-[120px] truncate">
                  {user?.fullName || user?.email?.split('@')[0]}
                </span>
              </div>
              <Badge variant="secondary" className="hidden sm:inline-flex text-[10px] px-1.5 py-0 h-5">
                Admin
              </Badge>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground rotate-90" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="font-normal">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-primary">
                    {getInitials(user?.fullName, user?.email)}
                  </span>
                </div>
                <div className="flex flex-col min-w-0">
                  <p className="text-sm font-medium truncate">{user?.fullName || 'Admin'}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  <Badge variant="secondary" className="w-fit mt-1 text-[10px] px-1.5 py-0 h-4">
                    {tAuth('systemAdmin')}
                  </Badge>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              className="text-destructive focus:text-destructive cursor-pointer"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {tAuth('logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
